// src/pages/ProfileConfirm.tsx
import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Loader2, MapPin, User, CheckCircle2, Phone } from 'lucide-react';
import { clientProfileAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { locationData } from '../data/locations';
import { useAuth } from '@/contexts/AuthContext';

type BuyerProfile = {
    id: number | string;
    first_name: string;
    last_name: string;
    phone_number: string;
    email: string;
    country_id?: string;
    province_id?: string;
    city_id?: string;
    address?: string;
    complexname?: string;
    complexnumber?: string; // 👈 شماره تلفن ثابت مجتمع / محل کسب
    status?: string;
    must_update_profile?: boolean;
    profile_confirmed_at?: string | null;
};

const ProfileConfirm: React.FC = () => {
    const [buyer, setBuyer] = useState<BuyerProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const navigate = useNavigate();
    const { updateClient } = useAuth();

    const [selectedCountryId, setSelectedCountryId] = useState<string>('IR');
    const [selectedProvinceId, setSelectedProvinceId] = useState<string>('');
    const [selectedCityId, setSelectedCityId] = useState<string>('');

    const countries = locationData.countries || [];
    const currentCountry = countries.find(c => c.id === selectedCountryId) || countries[0];
    const provinces = currentCountry?.provinces || [];
    const currentProvince = provinces.find(p => p.id === selectedProvinceId);
    const cities = currentProvince?.cities || [];

    // ------------------ Load Profile ------------------
    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                setErr('');
                const res = await clientProfileAPI.getProfile();
                if (!res?.success) {
                    throw new Error(res?.message || 'خطا در دریافت پروفایل');
                }

                const b: BuyerProfile = res.buyer;
                setBuyer(b);

                // اگر پروفایل قبلاً تکمیل شده، مستقیم بفرست داشبورد
                if (b.must_update_profile === false) {
                    // همزمان AuthContext رو هم آپدیت کن که همخوان باشه
                    updateClient({
                        ...b,
                        must_update_profile: false,
                    });
                    navigate('/dashboard', { replace: true });
                    return;
                }

                // ست‌کردن اولیه selectها از روی اطلاعات خریدار
                if (b.country_id) {
                    setSelectedCountryId(b.country_id);
                }
                if (b.province_id) {
                    setSelectedProvinceId(b.province_id);
                }
                if (b.city_id) {
                    setSelectedCityId(b.city_id);
                }
            } catch (e: any) {
                setErr(e?.response?.data?.message || e.message || 'خطا در دریافت اطلاعات پروفایل');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [navigate, updateClient]);

    // ------------------ Handlers ------------------
    const handleChange = (field: keyof BuyerProfile, value: string) => {
        setBuyer(prev => (prev ? { ...prev, [field]: value } : prev));
    };

    const handleCountryChange = (value: string) => {
        setSelectedCountryId(value);
        setSelectedProvinceId('');
        setSelectedCityId('');
        setBuyer(prev =>
            prev
                ? {
                    ...prev,
                    country_id: value,
                    province_id: undefined,
                    city_id: undefined,
                }
                : prev,
        );
    };

    const handleProvinceChange = (value: string) => {
        setSelectedProvinceId(value);
        setSelectedCityId('');
        setBuyer(prev =>
            prev
                ? {
                    ...prev,
                    province_id: value,
                    city_id: undefined,
                }
                : prev,
        );
    };

    const handleCityChange = (value: string) => {
        setSelectedCityId(value);
        setBuyer(prev =>
            prev
                ? {
                    ...prev,
                    city_id: value,
                }
                : prev,
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!buyer) return;

        setSaving(true);
        try {
            const res = await clientProfileAPI.confirmProfile({
                first_name: buyer.first_name,
                last_name: buyer.last_name,
                country_id: buyer.country_id || selectedCountryId,
                province_id: buyer.province_id || selectedProvinceId,
                city_id: buyer.city_id || selectedCityId,
                address: buyer.address,
                complexname: buyer.complexname,
                complexnumber: buyer.complexnumber, // 👈 شماره تلفن ثابت مجتمع
            });

            if (!res?.success) {
                throw new Error(res?.message || 'ذخیره پروفایل ناموفق بود.');
            }

            const updatedBuyer: BuyerProfile = res.buyer || {
                ...buyer,
                must_update_profile: false,
                country_id: buyer.country_id || selectedCountryId,
                province_id: buyer.province_id || selectedProvinceId,
                city_id: buyer.city_id || selectedCityId,
            };

            // 🔄 آپدیت AuthContext و localStorage
            updateClient({
                ...updatedBuyer,
                must_update_profile: false,
            });

            toast.success('اطلاعات شما با موفقیت ثبت شد ✨');
            navigate('/dashboard', { replace: true });
        } catch (e: any) {
            toast.error(e?.response?.data?.message || e.message || 'خطا در ذخیره اطلاعات.');
        } finally {
            setSaving(false);
        }
    };

    // ------------------ UI States ------------------
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-emerald-50" dir="rtl">
                <div className="text-center">
                    <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mx-auto mb-3" />
                    <p className="text-emerald-700 font-medium">در حال بارگذاری پروفایل...</p>
                </div>
            </div>
        );
    }

    if (err || !buyer) {
        return (
            <div className="p-6" dir="rtl">
                <Alert variant="destructive">
                    <AlertDescription>{err || 'خطا در دریافت پروفایل.'}</AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div
            dir="rtl"
            className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50 p-4"
        >
            <div className="w-full max-w-xl">
                <Card className="rounded-3xl shadow-xl bg-white/90 backdrop-blur-sm border-emerald-100">
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-2xl bg-emerald-100 text-emerald-700">
                                <CheckCircle2 className="h-6 w-6" />
                            </div>
                            <div>
                                <CardTitle className="text-emerald-900">تایید اطلاعات حساب</CardTitle>
                                <CardDescription className="text-emerald-700">
                                    لطفاً اطلاعات محل نصب / آدرس خود را بررسی و در صورت نیاز اصلاح کنید.
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>

                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* نام و نام خانوادگی */}
                            {/*<div className="grid grid-cols-1 md:grid-cols-2 gap-4">*/}
                            {/*    <div>*/}
                            {/*        <label className="block text-xs mb-1 text-gray-500">نام</label>*/}
                            {/*        <div className="relative">*/}
                            {/*            <Input*/}
                            {/*                value={buyer.first_name}*/}
                            {/*                onChange={e => handleChange('first_name', e.target.value)}*/}
                            {/*                className="pr-9 rounded-2xl"*/}
                            {/*            />*/}
                            {/*            <User className="absolute right-3 top-2.5 h-4 w-4 text-emerald-500" />*/}
                            {/*        </div>*/}
                            {/*    </div>*/}
                            {/*    <div>*/}
                            {/*        <label className="block text-xs mb-1 text-gray-500">نام خانوادگی</label>*/}
                            {/*        <Input*/}
                            {/*            value={buyer.last_name}*/}
                            {/*            onChange={e => handleChange('last_name', e.target.value)}*/}
                            {/*            className="rounded-2xl"*/}
                            {/*        />*/}
                            {/*    </div>*/}
                            {/*</div>*/}

                            {/* موبایل و ایمیل (فقط نمایش) */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                                <div>
                                    <div className="text-xs text-gray-500 mb-1">نام</div>
                                    <div className="px-3 py-2 rounded-2xl bg-gray-50 border border-gray-100 font-mono">
                                        {buyer.first_name}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 mb-1">نام خانوادگی</div>
                                    <div className="px-3 py-2 rounded-2xl bg-gray-50 border border-gray-100 truncate">
                                        {buyer.last_name}
                                    </div>
                                </div>
                            </div>
                            {/* موبایل و ایمیل (فقط نمایش) */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                                <div>
                                    <div className="text-xs text-gray-500 mb-1">شماره موبایل</div>
                                    <div className="px-3 py-2 rounded-2xl bg-gray-50 border border-gray-100 font-mono">
                                        {buyer.phone_number}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 mb-1">ایمیل</div>
                                    <div className="px-3 py-2 rounded-2xl bg-gray-50 border border-gray-100 truncate">
                                        {buyer.email}
                                    </div>
                                </div>
                            </div>

                            {/* کشور / استان / شهر */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs mb-1 text-gray-500">کشور</label>
                                    <select
                                        className="w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm bg-white"
                                        value={selectedCountryId}
                                        onChange={e => handleCountryChange(e.target.value)}
                                    >
                                        {countries.map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.name_native || c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs mb-1 text-gray-500">استان</label>
                                    <select
                                        className="w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm bg-white"
                                        value={selectedProvinceId}
                                        onChange={e => handleProvinceChange(e.target.value)}
                                    >
                                        <option value="">انتخاب استان</option>
                                        {provinces.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.name_native || p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs mb-1 text-gray-500">شهر</label>
                                    <select
                                        className="w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm bg-white"
                                        value={selectedCityId}
                                        onChange={e => handleCityChange(e.target.value)}
                                    >
                                        <option value="">انتخاب شهر</option>
                                        {cities.map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.name_native || c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* مجتمع / تلفن ثابت / آدرس */}
                            <div>
                                <label className="block text-xs mb-1 text-gray-500">نام مجتمع / پاساژ / مجموعه</label>
                                <Input
                                    value={buyer.complexname || ''}
                                    onChange={e => handleChange('complexname', e.target.value)}
                                    className="rounded-2xl"
                                    placeholder="نام مجتمع / پاساژ (در صورت وجود)"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="md:col-span-1">
                                    <label className="block text-xs mb-1 text-gray-500">
                                        شماره تلفن ثابت مجتمع / محل کسب
                                    </label>
                                    <div className="relative">
                                        <Input
                                            value={buyer.complexnumber || ''}
                                            onChange={e => handleChange('complexnumber', e.target.value)}
                                            className="pr-9 rounded-2xl"
                                            placeholder="مثلاً 011-xxxxxxx"
                                        />
                                        <Phone className="absolute right-3 top-2.5 h-4 w-4 text-emerald-500" />
                                    </div>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-xs mb-1 text-gray-500">آدرس کامل</label>
                                    <div className="relative">
                                        <Input
                                            value={buyer.address || ''}
                                            onChange={e => handleChange('address', e.target.value)}
                                            className="pr-9 rounded-2xl"
                                            placeholder="آدرس دقیق محل نصب / کسب‌وکار"
                                        />
                                        <MapPin className="absolute right-3 top-2.5 h-4 w-4 text-emerald-500" />
                                    </div>
                                </div>
                            </div>

                            <div className="pt-3 flex flex-col md:flex-row gap-3 md:gap-4 justify-between items-center">
                                <div className="text-xs text-gray-500">
                                    با تأیید این اطلاعات، حساب شما فعال شده و به همه بخش‌ها دسترسی خواهید داشت.
                                </div>
                                <Button
                                    type="submit"
                                    disabled={saving}
                                    className="rounded-full px-6 bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2"
                                >
                                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                                    ذخیره و فعال‌سازی حساب
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ProfileConfirm;
